import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, RotateCcw, Sparkles } from 'lucide-react';
import { TranslationNS } from '@gokrew/models/constants';
import type { DialogComponentProps } from '@gokrew/models/dialog';
import type { NeedsAttentionPrepareResult } from '@gokrew/models/company-dashboard';
import { Button } from '@/components/Button';
import { CopyButton } from '@/components/InviteSuccessDialog';
import { Skeleton } from '@/components/Skeleton';
import { useDialog } from '@/context/DialogContext';
import { fillPrepareInvitation } from '@/utility/needsAttention';
import styles from './NeedsAttentionPrepare.module.css';

export interface NeedsAttentionPrepareProps extends DialogComponentProps {
  /** Asks the server for the talking points (`POST .../prepare`). */
  load: () => Promise<NeedsAttentionPrepareResult>;
  /** Fills `{{firstName}}` in the invitation; never sent to the server. */
  firstName?: string | null;
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; result: NeedsAttentionPrepareResult };

/**
 * "Prepare the meeting" / "Préparer la rencontre" (spec 7.1): 3 to 5
 * talking points and a short invitation to the employee, each with a copy
 * button, and how they were drafted. The portal opens it in a dialog from a
 * check-in, vacation or overtime line. Copy: `needs_attention_prepare` in
 * `libsCommon`.
 */
export const NeedsAttentionPrepare = memo(
  ({ dialogId, load, firstName }: NeedsAttentionPrepareProps) => {
    const { t } = useTranslation(TranslationNS.LibsCommon);
    const { closeDialog } = useDialog();
    const [state, setState] = useState<LoadState>({ status: 'loading' });
    const alive = useRef(true);

    const run = useCallback(() => {
      setState({ status: 'loading' });
      load()
        .then((result) => {
          if (alive.current) setState({ status: 'ready', result });
        })
        .catch(() => {
          if (alive.current) setState({ status: 'error' });
        });
    }, [load]);

    useEffect(() => {
      alive.current = true;
      run();
      return () => {
        alive.current = false;
      };
    }, [run]);

    const close = () => {
      if (dialogId) closeDialog(dialogId);
    };

    if (state.status === 'loading') {
      return (
        <div className={styles.root} aria-busy="true">
          <p className={styles.loading}>
            <Sparkles className={styles.loadingIcon} aria-hidden="true" />
            {t('needs_attention_prepare.loading')}
          </p>
          <Skeleton className={styles.skeleton} />
          <Skeleton className={styles.skeleton} />
          <Skeleton className={styles.skeleton} />
        </div>
      );
    }

    if (state.status === 'error') {
      return (
        <div className={styles.root} role="alert">
          <p className={styles.error}>{t('needs_attention_prepare.error')}</p>
          <div className={styles.footer}>
            <Button variant="outline" size="sm" onClick={close}>
              {t('needs_attention_prepare.close')}
            </Button>
            <Button size="sm" IconPrefix={RotateCcw} onClick={run}>
              {t('needs_attention_prepare.retry')}
            </Button>
          </div>
        </div>
      );
    }

    const { result } = state;
    const invitation = fillPrepareInvitation(result.invitation, firstName);
    const points = result.talkingPoints.map((point) => `- ${point}`).join('\n');
    return (
      <div className={styles.root}>
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>
              {t('needs_attention_prepare.talking_points')}
            </h3>
            <CopyButton text={points} iconOnly={false} />
          </div>
          <ul className={styles.points}>
            {result.talkingPoints.map((point, index) => (
              <li key={index}>{point}</li>
            ))}
          </ul>
        </section>
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>
              {t('needs_attention_prepare.invitation')}
            </h3>
            <CopyButton text={invitation} iconOnly={false} />
          </div>
          <p className={styles.invitation}>{invitation}</p>
        </section>
        <p className={styles.disclosure}>
          <Info className={styles.disclosureIcon} aria-hidden="true" />
          {t(
            result.source === 'ai'
              ? 'needs_attention_prepare.disclosure_ai'
              : 'needs_attention_prepare.disclosure_template'
          )}
        </p>
        <div className={styles.footer}>
          <Button size="sm" onClick={close}>
            {t('needs_attention_prepare.close')}
          </Button>
        </div>
      </div>
    );
  }
);
NeedsAttentionPrepare.displayName = 'NeedsAttentionPrepare';
